import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  ListItemIcon,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { useTheme } from '@mui/material/styles'

const SelectButton = ({ text, icon, tooltip, onClick, options, ...restProps }) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const theme = useTheme()
  const handleToggle = () => setOpen(!open)

  const handleClose = option => event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    if (option) {
      onClick(option)
    }
    setOpen(false)
  }

  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const prevOpen = useRef(open)

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <Stack>
      <Tooltip title={tooltip} placement="left">
        <Button
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          {...restProps}
        >
          {icon}
          {text}
        </Button>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{ zIndex: 99 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose()}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  style={{ background: theme.palette.background.default }}
                >
                  {options.map(option => (
                    <MenuItem
                      key={`select-option-${option.action}`}
                      onClick={handleClose(option)}
                      disabled={!!option.disabled}
                    >
                      {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                      {option.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  )
}

SelectButton.propTypes = {
  tooltip: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.element,
  color: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      action: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onClick: PropTypes.func.isRequired,
}

SelectButton.defaultProps = {
  tooltip: '',
  icon: null,
  text: '',
  color: 'primary',
  options: [],
}

export default SelectButton
